import React, { useState, useEffect } from "react";
import { DatePicker, TimePicker, InputNumber } from "antd";
import Select from "react-select";
import dayjs from "dayjs";
import { cityOptions } from "./cityOptions";
import styles from "./SearchForm.module.css";

const idiomaOptions = [
  { value: "english", label: "English" },
  { value: "italian", label: "Italian" },
  { value: "french", label: "French" },
  { value: "german", label: "German" },
  { value: "portuguese", label: "Portuguese" },
  { value: "russian", label: "Russian" },
  { value: "hebrew", label: "Hebrew" },
  { value: "mandarin", label: "Mandarin" },
  { value: "japanese", label: "Japanese" },
  { value: "ukrainian", label: "Ukrainian" },
  { value: "spanish", label: "Spanish" },
  { value: "dutch", label: "Dutch" },
  { value: "swedish", label: "Swedish" },
  { value: "norwegian", label: "Norwegian" },
  { value: "danish", label: "Danish" },
  { value: "polish", label: "Polish" },
  { value: "greek", label: "Greek" },
  { value: "turkish", label: "Turkish" },
  { value: "czech", label: "Czech" },
  { value: "hungarian", label: "Hungarian" },
  { value: "romanian", label: "Romanian" },
  { value: "finnish", label: "Finnish" },
  { value: "slovak", label: "Slovak" },
  { value: "bulgarian", label: "Bulgarian" },
  { value: "croatian", label: "Croatian" },
  { value: "irish", label: "Irish" },
  { value: "welsh", label: "Welsh" },
  { value: "scottish_gaelic", label: "Scottish Gaelic" },
  { value: "basque", label: "Basque" },
  { value: "catalan", label: "Catalan" },
  { value: "galician", label: "Galician" },
];

interface FlightSearchProps {
  mostrarTodosFiltros?: boolean;
  servicos?: { id: number; nome: string }[];
  servicoSelecionado?: number | null;
  setServicoSelecionado?: (id: number) => void;
  onSearch: (filtros: {
    termo?: string;
    data?: string;
    hora?: string;
    idioma?: string;
    pessoas?: number;
    servicoId?: number;
  }) => void;
}

const FlightSearch: React.FC<FlightSearchProps> = ({
  mostrarTodosFiltros = false,
  servicos = [],
  servicoSelecionado,
  setServicoSelecionado,
  onSearch,
}) => {
  const [termo, setTermo] = useState("");
  const [data, setData] = useState<dayjs.Dayjs | null>(null);
  const [hora, setHora] = useState<dayjs.Dayjs | null>(null);
  const [idioma, setIdioma] = useState<string | undefined>();
  const [pessoas, setPessoas] = useState<number | undefined>();
  const BASE_URL = process.env.REACT_APP_API_URL;

  // 🔹 Fetch preferências apenas **uma vez**
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const fetchPrefs = async () => {
      try {
        const res = await fetch(`${BASE_URL}/preferencias`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const prefs = await res.json();
        if (prefs.servicoId && setServicoSelecionado) setServicoSelecionado(prefs.servicoId);
        if (prefs.idioma) setIdioma(prefs.idioma);
        if (prefs.pessoas) setPessoas(prefs.pessoas);
        if (prefs.cidade) setTermo(prefs.cidade);
      } catch (err) {
        console.error("Erro ao carregar preferências:", err);
      }
    };

    fetchPrefs();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch({
      termo,
      data: data?.format("YYYY-MM-DD"),
      hora: hora?.format("HH:mm"),
      idioma,
      pessoas,
      servicoId: servicoSelecionado || undefined,
    });
  };

  return (
    <div className={styles.bannerForm}>
      {/* Serviços */}
      {servicos.length > 0 && setServicoSelecionado && (
        <div className={styles.cardHeader}>
          <ul className="nav">
            {servicos.map((s) => (
              <li key={s.id}>
                <a
                  className={`${styles.navLink} ${
                    servicoSelecionado === s.id ? styles.navLinkActive : ""
                  }`}
                  onClick={() => setServicoSelecionado(s.id)}
                >
                  {s.nome}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Formulário */}
      <div className="card-body">
        <form
          onSubmit={handleSubmit}
          className="d-flex flex-wrap justify-content-between gap-3"
        >
          {/* City */}
          <div className={styles.formSquare + " flex-fill"}>
            <label className={styles.formLabel}>City</label>
            <Select
              options={cityOptions}
              value={cityOptions.find((c) => c.value === termo) || null}
              onChange={(selectedOption) => setTermo(selectedOption?.value || "")}
              placeholder="Select a city"
              isClearable
              classNamePrefix="react-select"
            />
          </div>

          {/* Date */}
          <div className={styles.formSquare + " flex-fill"}>
            <label className={styles.formLabel}>Date</label>
            <DatePicker
              value={data}
              onChange={setData}
              format="DD/MM/YYYY"
              className="form-control"
              allowClear
            />
          </div>

          {/* Time */}
          <div className={styles.formSquare + " flex-fill"}>
            <label className={styles.formLabel}>Time</label>
            <TimePicker
              value={hora}
              onChange={setHora}
              format="HH:mm"
              className="form-control"
              allowClear
            />
          </div>

          {/* Language */}
          {mostrarTodosFiltros && (
            <div className={styles.formSquare + " flex-fill"}>
              <label className={styles.formLabel}>Language</label>
              <select
                className="form-control"
                value={idioma}
                onChange={(e) => setIdioma(e.target.value)}
              >
                <option value="">Select</option>
                {idiomaOptions.map((i) => (
                  <option key={i.value} value={i.value}>
                    {i.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* People */}
          {mostrarTodosFiltros && (
            <div className={styles.formSquare + " flex-fill"}>
              <label className={styles.formLabel}>People</label>
              <InputNumber
                min={1}
                value={pessoas}
                onChange={(v) => setPessoas(v || undefined)}
              />
            </div>
          )}

          {/* Submit */}
          <div className="form-square flex-fill d-flex align-items-end">
            <button type="submit" className={`${styles.submitBtn} w-100 rounded`}>
              Search
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FlightSearch;